import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { ACCESS_FLOW_LOCK_STORAGE_KEY, APP_ENTRY_ROUTE, clearAccessFlowLock, clearSocialAuthPending, getAccessFlowLock, getSocialAuthPending, sanitizeInternalRedirect } from "@/lib/access-flow";
import { clearPersistedSession } from "@/lib/sessionPersistence";
import { invalidateUserBootstrap } from "@/lib/user-bootstrap";
import { traceRouteEvent } from "@/lib/route-debug-trace";
import { consumeVoluntarySignout, markSessionExpired, markVoluntarySignout } from "@/lib/session-expired-notice";
import { tearDownLocalSession } from "@/lib/auth/teardown";
import { clearLastActive, isInactivityExpired, touchLastActive } from "@/lib/auth/inactivity";
import { startSessionKeepAlive } from "@/lib/auth/sessionKeepAlive";
import { trackEvent as trackAnalyticsEvent, identifyUser, resetAnalytics } from "@/lib/posthog";

import { releaseAccountSession } from "@/lib/auth/accountSession";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, name: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const AUTH_BOOTSTRAP_TIMEOUT_MS = 6000;

/**
 * Validação local do access_token recuperado do storage.
 *
 * Aceita APENAS JWTs estruturalmente válidos (3 partes base64url cujo
 * header e payload são JSON parseáveis) E não expirados (`exp` no futuro,
 * com 30s de tolerância). Tokens adulterados / corrompidos / expirados
 * NUNCA podem ser tratados como sessão válida — Supabase getSession()
 * apenas lê do storage e não valida o token, então essa checagem precisa
 * acontecer aqui antes de propagar `user` para os Guards.
 */
const isStructurallyValidSession = (accessToken: unknown): boolean => {
  if (typeof accessToken !== "string" || !accessToken) return false;
  const parts = accessToken.split(".");
  if (parts.length !== 3) return false;
  try {
    const decode = (segment: string) => {
      if (!segment) throw new Error("empty segment");
      const padded = segment.replace(/-/g, "+").replace(/_/g, "/");
      const padLen = (4 - (padded.length % 4)) % 4;
      const b64 = padded + "=".repeat(padLen);
      const json = typeof atob === "function"
        ? atob(b64)
        : Buffer.from(b64, "base64").toString("utf-8");
      return JSON.parse(json) as Record<string, unknown>;
    };
    decode(parts[0]);
    const payload = decode(parts[1]);
    const exp = typeof payload.exp === "number" ? payload.exp : null;
    if (exp !== null && exp * 1000 < Date.now() - 30_000) return false;
    return true;
  } catch {
    return false;
  }
};

/**
 * Demolição síncrona da sessão local quando o refresh falha (rede caída,
 * gateway down, 4xx, 5xx, token adulterado). Os Guards reagem em < 1 tick
 * porque imediatamente:
 *   1. limpa o lock de fluxo + storage persistente
 *   2. dispara o banner `session-expired` com motivo informado
 *   3. faz signOut() com cap de 1500ms (não pode travar a UI se a rede
 *      sumiu — o /logout também pode estar offline)
 *   4. replace() para a entry pública pra remover qualquer rota protegida
 *      ainda montada sem depender do ciclo do React Router
 *
 * Idempotente: se já houver uma navegação em andamento, o segundo replace
 * é no-op. Seguro chamar de qualquer handler (bootstrap, onAuthStateChange,
 * .catch de getSession).
 */
// tearDownLocalSession movido para src/lib/auth/teardown.ts (compartilhado
// com o fetch interceptor instalado em main.tsx, que precisa derrubar a
// sessão quando supabase-js engole o evento de refresh falho).




export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let bootstrapped = false;
    let lastTrackedUserId: string | null = null;
    let pendingTimeoutId: number | null = null;
    let keepAliveCleanup: (() => void) | null = null;

    // Snapshot: havia token persistido ANTES do bootstrap? Se sim e o
    // bootstrap resolver sem sessão, foi refresh involuntário.
    let hadPersistedSession = false;
    try {
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i) ?? "";
        if (/^sb-.+-auth-token$/.test(k)) {
          hadPersistedSession = true;
          break;
        }
      }
    } catch {
      /* storage indisponível */
    }


    const applySession = (nextSession: Session | null) => {
      if (!active) return;
      if (bootstrapTimeoutId !== null) {
        window.clearTimeout(bootstrapTimeoutId);
        bootstrapTimeoutId = null;
      }
      traceRouteEvent("auth:applySession", window.location.pathname + window.location.search, {
        hasUser: Boolean(nextSession?.user),
        user: nextSession?.user ? "present" : "none",
      });
      const nextUserId = nextSession?.user?.id ?? null;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setLoading(false);

      // Inicia/para keep-alive de sessão para evitar expiração do access token.
      if (nextSession?.access_token) {
        if (!keepAliveCleanup) {
          keepAliveCleanup = startSessionKeepAlive();
        }
      } else {
        keepAliveCleanup?.();
        keepAliveCleanup = null;
      }

      // PostHog: identifica/reseta de acordo com mudança de sessão
      if (nextUserId && nextUserId !== lastTrackedUserId) {
        void identifyUser(nextUserId, { email: nextSession?.user?.email });
        lastTrackedUserId = nextUserId;
      } else if (!nextUserId && lastTrackedUserId) {
        void resetAnalytics();
        lastTrackedUserId = null;
      }
    };

    let bootstrapTimeoutId: number | null = window.setTimeout(() => {
      // Failsafe: se nem getSession nem onAuthStateChange resolverem em 6s,
      // assume sem sessão para não travar a UI eternamente em loading.
      if (!bootstrapped) {
        bootstrapped = true;
        applySession(null);
      }
    }, AUTH_BOOTSTRAP_TIMEOUT_MS);

    const resolveAuthState = (nextSession: Session | null) => {
      if (pendingTimeoutId !== null) {
        window.clearTimeout(pendingTimeoutId);
        pendingTimeoutId = null;
      }

      if (nextSession?.user) {
        clearSocialAuthPending();
      }

      bootstrapped = true;
      applySession(nextSession);
    };

    if (getSocialAuthPending()) {
      pendingTimeoutId = window.setTimeout(() => {
        clearSocialAuthPending();
        bootstrapped = true;
        applySession(null);
      }, 15000);
    }

    let hadUserBefore = false;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      traceRouteEvent("auth:onAuthStateChange", window.location.pathname + window.location.search, {
        event,
        hasUser: Boolean(nextSession?.user),
        bootstrapped,
      });

      // Bootstrap gate: enquanto getSession() não tiver concluído, NENHUM
      // evento do onAuthStateChange pode flipar loading=false nem alterar
      // a sessão. Isso evita que ProtectedRoute/RoleGuard tomem decisão de
      // redirect baseada em um INITIAL_SESSION nulo que chegou antes do
      // getSession() ler o token persistido no storage.
      if (!bootstrapped) {
        // Se o evento veio COM sessão, sincroniza o flag de "havia usuário"
        // para que o próximo SIGNED_OUT seja corretamente classificado,
        // mas mantém loading=true até o getSession() ser a fonte de verdade.
        if (nextSession?.user) hadUserBefore = true;
        return;
      }

      // Detecta signOut INVOLUNTÁRIO (refresh token rejeitado, revogação
      // remota, ban). Voluntário (botão "sair") consome o marker antes.
      if (event === "SIGNED_OUT" && hadUserBefore) {
        const voluntary = consumeVoluntarySignout();
        if (!voluntary) {
          void tearDownLocalSession("refresh_failed");
          hadUserBefore = false;
          bootstrapped = true;
          applySession(null);
          return;
        }
      }
      // TOKEN_REFRESHED com sessão nula = refresh falhou silenciosamente.
      if (event === "TOKEN_REFRESHED" && !nextSession && hadUserBefore) {
        void tearDownLocalSession("refresh_failed");
        hadUserBefore = false;
        bootstrapped = true;
        applySession(null);
        return;
      }
      // TOKEN_REFRESHED com sessão presente mas adulterada/expirada:
      // mesma defesa do bootstrap — não confiar no token novo.
      if (event === "TOKEN_REFRESHED" && nextSession && !isStructurallyValidSession(nextSession.access_token)) {
        void tearDownLocalSession("refresh_failed");
        hadUserBefore = false;
        bootstrapped = true;
        applySession(null);
        return;
      }

      if (nextSession?.user) touchLastActive();
      hadUserBefore = Boolean(nextSession?.user);
      resolveAuthState(nextSession);
    });

    supabase.auth.getSession()
      .then(async ({ data: { session }, error }) => {
        if (!active) return;
        hadUserBefore = Boolean(session?.user);

        // Erro retornado pelo SDK (refresh implícito falhou dentro de
        // getSession) — trata como sessão morta independentemente do
        // que veio no `session`.
        if (error) {
          void tearDownLocalSession("refresh_failed");
          bootstrapped = true;
          hadUserBefore = false;
          applySession(null);
          return;
        }

        if (session?.user) {
          // Defesa contra JWT adulterado / expirado persistido no storage.
          // getSession() apenas lê do disco — não valida assinatura nem exp.
          // Se o token for estruturalmente inválido ou já estiver expirado,
          // descartamos a sessão localmente e sinalizamos `refresh_failed`
          // antes que qualquer Guard veja `user` populado e libere children.
          if (!isStructurallyValidSession(session.access_token)) {
            void tearDownLocalSession("refresh_failed");
            bootstrapped = true;
            hadUserBefore = false;
            applySession(null);
            return;
          }
          // Logout automático por inatividade (24h sem abrir o app).
          if (isInactivityExpired()) {
            clearLastActive();
            void tearDownLocalSession("refresh_failed");
            bootstrapped = true;
            hadUserBefore = false;
            applySession(null);
            return;
          }
          touchLastActive();
          resolveAuthState(session);
          return;
        }

        if (getSocialAuthPending()) {
          // Mantém loading=true até o pendingTimeoutId resolver.
          return;
        }

        // Havia token persistido, mas getSession retornou null →
        // refresh involuntário falhou. Demolição completa + redirect.
        if (hadPersistedSession) {
          void tearDownLocalSession("refresh_failed");
          bootstrapped = true;
          applySession(null);
          return;
        }

        bootstrapped = true;
        applySession(session);
      })
      .catch(() => {
        if (!getSocialAuthPending()) {
          if (hadPersistedSession) {
            void tearDownLocalSession("refresh_failed");
          }
          bootstrapped = true;
          applySession(null);
        }
      });




    // Isolamento entre abas: se outra aba trocou o lock de fluxo (ex.: usuário
    // logou como venue/admin em outra aba), forçamos signOut nesta aba para
    // evitar uso simultâneo de fluxos diferentes na mesma máquina.
    let lastKnownFlow = getAccessFlowLock();
    const isSupabaseAuthKey = (key: string | null) =>
      !!key && key.startsWith("sb-") && key.endsWith("-auth-token");
    const handleStorage = (e: StorageEvent) => {
      // Frente 8: propagação de logout multi-aba. Se outra aba removeu (ou
      // esvaziou) a chave de sessão do supabase-js, derrubamos a sessão
      // local IMEDIATAMENTE para que esta aba não continue mostrando UI
      // protegida com credenciais já revogadas.
      if (isSupabaseAuthKey(e.key)) {
        const cleared = e.newValue === null || e.newValue === "" || e.newValue === "null";
        if (cleared) {
          void tearDownLocalSession("remote_revoked");
          applySession(null);
        }
        return;
      }
      if (e.key !== ACCESS_FLOW_LOCK_STORAGE_KEY) return;
      const next = (e.newValue === "user" || e.newValue === "venue" || e.newValue === "admin") ? e.newValue : null;
      if (lastKnownFlow && next && next !== lastKnownFlow) {
        // Cross-flow detectado: derruba sessão local em silêncio.
        supabase.auth.signOut().catch(() => {});
        clearAccessFlowLock();
        clearPersistedSession();
        applySession(null);
        try { window.location.replace(APP_ENTRY_ROUTE); } catch { /* ignore */ }
      }
      lastKnownFlow = next;
    };
    window.addEventListener("storage", handleStorage);
    const handleSessionInvalidated = () => applySession(null);
    window.addEventListener("zappy:session-invalidated", handleSessionInvalidated);

    // Frente 13 — bfcache restore. Quando o usuário volta via gesto do
    // navegador (Voltar) numa página servida do bfcache, NENHUM
    // re-render acontece e a sessão em memória pode estar revogada.
    // Revalidamos via getSession e disparamos teardown se vier vazia.
    const handlePageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      if (isInactivityExpired()) {
        clearLastActive();
        void tearDownLocalSession("refresh_failed");
        applySession(null);
        return;
      }
      supabase.auth.getSession()
        .then(({ data, error }) => {
          if (!active) return;
          if (error || !data.session?.user) {
            void tearDownLocalSession("refresh_failed");
            applySession(null);
            return;
          }
          if (!isStructurallyValidSession(data.session.access_token)) {
            void tearDownLocalSession("refresh_failed");
            applySession(null);
            return;
          }
          touchLastActive();
        })
        .catch(() => {
          if (!active) return;
          void tearDownLocalSession("refresh_failed");
          applySession(null);
        });
    };
    window.addEventListener("pageshow", handlePageShow);

    // Frente 10 — network recovery. Quando a conexão volta (online) ou
    // a aba reganha foco após pausa, revalidamos a sessão para detectar
    // tokens que expiraram enquanto estávamos offline / em background.
    const revalidateSession = () => {
      // Logout automático por inatividade: se o app ficou ≥24h sem ser
      // aberto, derruba a sessão local mesmo que o refresh ainda funcione.
      if (hadUserBefore && isInactivityExpired()) {
        clearLastActive();
        void tearDownLocalSession("refresh_failed");
        applySession(null);
        return;
      }
      supabase.auth.getSession()
        .then(({ data, error }) => {
          if (!active) return;
          if (error || !data.session?.user) {
            // Só derruba se HAVIA sessão antes — não confunde com fluxo anônimo.
            if (hadUserBefore) {
              void tearDownLocalSession("refresh_failed");
              applySession(null);
            }
            return;
          }
          if (!isStructurallyValidSession(data.session.access_token)) {
            void tearDownLocalSession("refresh_failed");
            applySession(null);
            return;
          }
          touchLastActive();
        })
        .catch(() => { /* offline ainda — ignora, próximo evento tenta de novo */ });
    };
    const handleOnline = () => revalidateSession();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") revalidateSession();
    };
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      active = false;
      if (pendingTimeoutId !== null) {
        window.clearTimeout(pendingTimeoutId);
      }
      if (bootstrapTimeoutId !== null) {
        window.clearTimeout(bootstrapTimeoutId);
      }
      keepAliveCleanup?.();
      keepAliveCleanup = null;
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("zappy:session-invalidated", handleSessionInvalidated);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
      subscription.unsubscribe();
    };
  }, []);

  const signUp = useCallback(async (email: string, password: string, name: string) => {
    const { data: current, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || current.session) return { error: { message: "Saia da conta atual antes de criar outra conta." } };
    const normalizedEmail = email.trim().toLowerCase();
    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        data: { name, access_flow: getAccessFlowLock() === "venue" ? "venue" : "user" },
        emailRedirectTo: window.location.origin,
      },
    });

    if (error) {
      const msg = error.message || "";
      // Duplicidade de e-mail
      if (/already registered|already exists|duplicate|user.*exists/i.test(msg)) {
        return { error: { ...error, message: "Este e-mail já está cadastrado. Faça login ou recupere sua senha." } };
      }
      // Senha fraca / vazada (HIBP)
      if ((error as any)?.code === "weak_password" || /weak.?password|pwned|known to be weak|easy to guess/i.test(msg)) {
        return { error: { ...error, message: "Esta senha aparece em vazamentos públicos conhecidos e não pode ser usada. Escolha uma combinação única, com letras maiúsculas, minúsculas, números e símbolos." } };
      }
      if (/password.*(short|at least|6 characters|too short)/i.test(msg)) {
        return { error: { ...error, message: "A senha precisa ter pelo menos 6 caracteres." } };
      }
      if (/invalid.*email|email.*invalid|email address.*invalid/i.test(msg)) {
        return { error: { ...error, message: "E-mail inválido. Verifique o endereço digitado." } };
      }
      if (/signup.*disabled|signups.*not allowed/i.test(msg)) {
        return { error: { ...error, message: "O cadastro está temporariamente desativado. Tente novamente em instantes." } };
      }
      if (/rate limit|too many|over.?email.?send.?rate/i.test(msg)) {
        return { error: { ...error, message: "Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente novamente." } };
      }
      return { error };
    }

    // Proteção contra "obfuscated user" do Supabase (auto_confirm com e-mail já existente):
    // quando o e-mail já existe, identities vem como array vazio.
    const identities = data?.user?.identities;
    if (data?.user && Array.isArray(identities) && identities.length === 0) {
      await supabase.auth.signOut().catch(() => {});
      return {
        error: {
          name: "EmailAlreadyRegistered",
          message: "Este e-mail já está cadastrado. Faça login ou recupere sua senha.",
        } as any,
      };
    }

    void trackAnalyticsEvent("auth_signup", {
      method: "email",
      user_id: data?.user?.id,
    });
    return { error: null };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    try {
      const { data: current, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || current.session) return { error: { message: "Saia da conta atual antes de entrar em outra conta." } };
      const { error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });

      if (error) {
        const msg = error.message || "";
        if (/email not confirmed|email_not_confirmed/i.test(msg)) {
          // Reenvia o e-mail de confirmação para facilitar a vida do usuário
          await supabase.auth.resend({
            type: "signup",
            email: normalizedEmail,
            options: { emailRedirectTo: window.location.origin },
          }).catch(() => {});
          return {
            error: {
              ...error,
              message:
                "Seu e-mail ainda não foi confirmado. Reenviamos o link de confirmação — verifique sua caixa de entrada e a pasta de spam.",
            },
          };
        }
        if (/invalid login credentials|invalid_grant/i.test(msg)) {
          return {
            error: { ...error, message: "E-mail ou senha incorretos." },
          };
        }
        return { error };
      }

      void trackAnalyticsEvent("auth_login", { method: "email" });
      return { error: null };
    } catch (err: any) {
      // Falhas de rede aparecem como "Load failed" / "Failed to fetch"
      const message = err?.message || "";
      if (/load failed|failed to fetch|networkerror/i.test(message)) {
        return {
          error: {
            name: "NetworkError",
            message:
              "Sem conexão com o servidor. Verifique sua internet e tente novamente.",
          } as any,
        };
      }
      return { error: { name: "SignInError", message: message || "Erro ao entrar." } as any };
    }
  }, []);

  const signOut = useCallback(async () => {
    markVoluntarySignout();
    await Promise.race([releaseAccountSession().catch(() => undefined), new Promise(resolve => setTimeout(resolve, 1500))]);
    setUser(null);
    setSession(null);
    setLoading(false);
    clearAccessFlowLock();
    clearSocialAuthPending();
    clearPersistedSession();
    clearLastActive();
    invalidateUserBootstrap();
    const { error } = await supabase.auth.signOut({ scope: "local" });
    if (error) await supabase.auth.signOut({ scope: "local" });
    // Sanitização defensiva: signOut SEMPRE devolve uma rota interna válida.
    // Mesmo que APP_ENTRY_ROUTE fosse alterado no futuro para algo inseguro,
    // o sanitizador único garante que apenas paths internos são propagados.
    return sanitizeInternalRedirect(APP_ENTRY_ROUTE) ?? "/";
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
